import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const JOB_PORTAL_URL = 'https://jobportal.studentforhire.ca';
export const SITE_URL = 'https://studentforhire.ca';
export const PHONE_HREF = 'tel:+14168062390';
export const PHONE_LABEL = '(416) 806-2390';
export const CONTACT_EMAIL = 'info@studentforhire.ca';

export const DEFAULT_TITLE = 'Student For Hire';
export const DEFAULT_DESCRIPTION = 'Connecting Students with Landscaping and Seasonal Services for Your Home.';

// `slug` is the value SendMail.php has always received as serviceType.
export const SERVICES = [
  { key: 'mow', slug: 'lawn-mowing', label: 'Lawn Mowing', Icon: MowerIcon },
  { key: 'weed', slug: 'weeding/gardening', label: 'Weeding & Gardening', Icon: LeafIcon },
  { key: 'leaf', slug: 'leaf-cleanup', label: 'Leaf Cleanups', Icon: LeavesIcon },
  { key: 'window', slug: 'window-cleaning', label: 'Window Washing', Icon: WindowIcon },
  { key: 'snow', slug: 'snow-removal', label: 'Snow Removal', Icon: SnowIcon },
  { key: 'pressure', slug: 'power-washing', label: 'Pressure Washing', Icon: SprayIcon },
  { key: 'furniture', slug: 'furniture-moving', label: 'Furniture Moving', Icon: SofaIcon },
];

export const SOCIAL_LINKS = [
  { label: 'Email', href: `mailto:${CONTACT_EMAIL}` },
  { label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61560992471558' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/studentforhire' },
  { label: 'Instagram', href: 'https://www.instagram.com/studentforhire/' },
  { label: 'TikTok', href: 'https://www.tiktok.com/@studentforhire' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UCsYSq-9nRYfGqmBVQSdcFXg' },
];

// Per-route head tags. react-helmet 6 loses its state under React 19 StrictMode
// (and then strips the description), so this updates index.html's tags in place.
export function Seo({ title = DEFAULT_TITLE, description = DEFAULT_DESCRIPTION, canonical }) {
  useEffect(() => {
    document.title = title;

    let meta = document.head.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);

    if (!canonical) return undefined;
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', canonical);
    document.head.appendChild(link);
    return () => link.remove();
  }, [title, description, canonical]);

  return null;
}

// Content renders after load, so the browser's own jump to #fragment can miss.
export function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView({ behavior: 'instant' });
  }, [hash]);
}

export function Wordmark({ className }) {
  return <img src="/logo-wordmark.png" alt="Student For Hire" width="1254" height="323" className={className} />;
}

export function SiteHeader({ logoTo = '/', children }) {
  return (
    <header className="site-header">
      <Link to={logoTo} className="site-header__logo">
        <Wordmark />
      </Link>
      <nav className="site-header__nav" aria-label="Main">
        {children}
      </nav>
    </header>
  );
}

export function PhoneLink() {
  return (
    <a href={PHONE_HREF} className="phone-link" aria-label={`Call ${PHONE_LABEL}`}>
      <PhoneIcon />
      <span className="phone-link__number">{PHONE_LABEL}</span>
    </a>
  );
}

export function SiteFooter({ crossLink }) {
  return (
    <footer className="site-footer">
      <div className="site-footer__row">
        <Wordmark className="site-footer__logo" />
        <nav className="site-footer__links" aria-label="Footer">
          <Link to="/">Home</Link>
          <Link to={crossLink.to}>{crossLink.label}</Link>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </nav>
      </div>
      <div className="site-footer__row">
        <span className="site-footer__social-label">Follow us</span>
        <div className="site-footer__links">
          {SOCIAL_LINKS.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer">{s.label}</a>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* Icons: paths copied from the design mockup. They inherit `color`. */

function Svg({ size = 24, strokeWidth = 1.6, children, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
      {children}
    </svg>
  );
}

export function ArrowIcon({ size = 18 }) {
  return <Svg size={size} strokeWidth={2.6}><path d="M5 12h14M13 6l6 6-6 6" /></Svg>;
}

export function CheckIcon({ size = 16, strokeWidth = 3 }) {
  return <Svg size={size} strokeWidth={strokeWidth}><path d="M20 6L9 17l-5-5" /></Svg>;
}

export function PhoneIcon() {
  return (
    <Svg size={16} strokeWidth={2.2} stroke="#34A353">
      <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8 9.8a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.7.7a2 2 0 011.7 2z" />
    </Svg>
  );
}

function MowerIcon({ size }) {
  return (
    <Svg size={size}>
      <path d="M4 16v-3a2 2 0 012-2h9l3 5" /><path d="M15 11l4-8h2" />
      <circle cx="7" cy="17" r="2.5" /><circle cx="17" cy="17" r="2.5" />
    </Svg>
  );
}

function LeafIcon({ size }) {
  return <Svg size={size}><path d="M11 20A7 7 0 019 6c4-2 9-2 11-2 0 2 0 7-2 11a7 7 0 01-7 5z" /><path d="M4 21c3-4 7-8 11-10" /></Svg>;
}

function LeavesIcon({ size }) {
  return (
    <Svg size={size}>
      <path d="M3 17c0-3 3-5 6-5 0 3-3 5-6 5z" /><path d="M21 17c0-3-3-5-6-5 0 3 3 5 6 5z" />
      <path d="M12 14c-2-3-1-7 2-9 1 3 0 7-2 9z" /><path d="M3 20h18" />
    </Svg>
  );
}

function WindowIcon({ size }) {
  return <Svg size={size}><rect x="4" y="3" width="16" height="18" rx="1" /><path d="M12 3v18M4 12h16" /></Svg>;
}

function SnowIcon({ size }) {
  return <Svg size={size}><path d="M12 2v20M3.3 7l17.4 10M20.7 7L3.3 17" /><path d="M9 4l3 2 3-2M9 20l3-2 3 2" /></Svg>;
}

function SprayIcon({ size }) {
  return (
    <Svg size={size}>
      <path d="M4 20l8-8" /><path d="M10 10l4 4" /><path d="M14 10l3-3" />
      <path d="M19 3v2M21 5h-2M21.5 2.5l-1.2 1.2" />
    </Svg>
  );
}

function SofaIcon({ size }) {
  return (
    <Svg size={size}>
      <path d="M5 11V7a2 2 0 012-2h10a2 2 0 012 2v4" /><path d="M3 13a2 2 0 014 0v2h10v-2a2 2 0 014 0v5H3z" />
      <path d="M5 18v2M19 18v2" />
    </Svg>
  );
}
